import "react-quill/dist/quill.snow.css";
import "quill-emoji/dist/quill-emoji.css";

import * as Emoji from "quill-emoji";
import QuillMention from "quill-mention";
import React, { useCallback } from "react";
import ReactQuill, { Quill, Range } from "react-quill";
// We are importing Quill from quill instead of react-quill because, the last one has some issues with typescript
import { Quill as PlainQuill } from "quill";
import TurndownService from "turndown";
import { PersonalizationValue } from "@dots-projects/types";

const icons = ReactQuill.Quill.import("ui/icons");
icons["code-block"] = `
  <svg class="quill-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M20 3H4C2.897 3 2 3.897 2 5V19C2 20.103 2.897 21 4 21H20C21.103 21 22 20.103 22 19V5C22 3.897 21.103 3 20 3ZM4 19V7H20L20.002 19H4Z"/>
  <path d="M9.293 9.293L5.586 13L9.293 16.707L10.707 15.293L8.414 13L10.707 10.707L9.293 9.293ZM14.707 9.293L13.293 10.707L15.586 13L13.293 15.293L14.707 16.707L18.414 13L14.707 9.293Z"/>
  </svg>
`;

type Member = {
  text: string;
  value: string;
  id: string;
  avatar: string;
};
type Channel = {
  text: string;
  value: string;
  id: string;
};

type Props = {
  members: Member[]; // an array of member objects {id: text, value: text, image: text} where id is the slack id and value is name
  channels: Channel[]; // an array of channel objects {id: text, value: text} where id is the slack id and value is name
  onChange: ({ html, text }: { html: string; text: string }) => void; // a callback function used when editor value is changed, function should take arg { html: string, text: string }
  value: string; // the default value to show in the Editor's text area
  disablePersonalTokens: boolean; // disable personalization tokens e.g. {{first_name}}
  templateText?: string; // the template text to be inserted
  platform: "slack" | "discord"; // the current platform: slack or discord
  children?: any; //Dynamic components that are rendered inside the editor
  refProp?: any; // a ref to the editor component that can be used outside of the editor
  personalizationValues?: PersonalizationValue[]; // Check PersonalizationValue.ts definition
  readOnly?: boolean; // If true, the editor will be read only
  refCallback?: any;
};

const spanRegex = /{{\D*}}/;

Quill.register("modules/mentions", QuillMention);
Quill.register("modules/emoji", Emoji);
Quill.register(
  "modules/cursor-position",
  function (
    quill: PlainQuill,
    options: {
      refProp: { current: { cursorPos: Range } };
    }
  ) {
    quill.on("selection-change", function (range: Range) {
      if (range && options.refProp.current) {
        options.refProp.current.cursorPos = range;
      }
    });

    quill.on("text-change", function () {
      const selection = quill.getSelection();
      if (quill.getSelection() && options.refProp.current) {
        options.refProp.current.cursorPos = selection;
      }
    });
  }
);

Quill.register("modules/remove-hyperlinks", function (quill: PlainQuill) {
  quill.on("text-change", function () {
    const evt = quill.getContents();
    let nodesWithLink = false;
    evt &&
      evt.ops?.forEach((op: any) => {
        if (op.attributes?.link) {
          delete op.attributes.link;
          nodesWithLink = true;
        }
      });
    if (nodesWithLink) {
      quill.setContents(evt);
    }
  });
});

/**
 * Editor that replicates Discord's text editor functionality, using QuillJS and plugins.
 * @param props @see Props
 * @returns component
 */
function Editor(props: Props) {
  const turndownService = new TurndownService();

  turndownService.addRule("strikethrough", {
    filter: ["del", "s"],
    replacement: (content: string) => `~~${content}~~`,
  });

  // The current implementation add two \n\n instead of one \n, causing extra line when the message is sent to discord,
  // for that reason we need to remove the extra line when the message is sent to discord
  turndownService.addRule("p", {
    filter: "p",
    replacement: function (content) {
      return `${content}\n`;
    },
  });

  // The current implementation add two \n\n instead of one \n, causing extra line when the message is sent to discord,
  // for that reason we need to remove the extra line when the message is sent to discord
  turndownService.addRule("blockquote", {
    filter: "blockquote",
    replacement: function (content) {
      return `> ${content}\n`;
    },
  });

  // When a property has a "_" on it turndown add a \ before to scape it, this is to avoid this behavior
  // check https://github.com/mixmark-io/turndown/issues/324 for more info
  turndownService.addRule("span", {
    filter: "span",
    replacement: function (content) {
      if (spanRegex.test(content)) {
        return content.replace("\n", "").replace("\\", "");
      }
      return `${content}`;
    },
  });

  const parser = new DOMParser();

  const mentionModule = {
    mentionDenotationChars: ["{{", " @", "#"],
    allowedChars: /^[A-Za-z\sÅÄÖåäö\-_@]*$/,
    spaceAfterInsert: true,
    renderItem: useCallback(
      // function to filter @ mentions and # channel links
      (item, searchTerm: string) => {
        if (props.platform === "slack") {
          if (item.avatar) {
            return `<div class="flex flex-row"><div class="mr-2 p-1"><img width="32" class="rounded" src="${item.avatar}"/></div><div>${item.value}</div></div>`;
          }
          if (searchTerm.startsWith("@")) {
            return `<div class="flex flex-row"><div class="mr-2 p-1"><img width="32" class="rounded" src="avatar_slack.png"/></div><div>${item.value}</div></div>`;
          }
          return `<div class="flex flex-row"><div class="mr-2 p-1"></div><div>${item.value}</div></div>`;
        }
        if (item.avatar) {
          return `<div class="flex flex-row"><div class="mr-2 p-1"><img width="32" class="rounded" src="https://cdn.discordapp.com/avatars/${item.id}/${item.avatar}.png"/></div><div>${item.value}</div></div>`;
        }

        if (searchTerm.startsWith("{")) {
          return `<div class="flex flex-row items-center">
                      <div class="p-1 shrink-0" style="flex-shrink: 0;">
                        <img width="16" class="rounded" src="/code.svg"/>
                      </div>
                      <div class="break-all leading-7">${
                        item.userText || item.value
                      }</div>
                   </div>`;
        }
        return `${item.userText || item.value}</div></div>`;
      },
      [props.members, props.channels]
    ),
    source: useCallback(
      // function to filter @ mentions and # channel links
      (searchTerm, renderList, mentionChar) => {
        let values: Member[] | Channel[] | PersonalizationValue[] | [] = [];

        // toggle which list to filter based on mention character
        if (mentionChar === " @") {
          values = props.members;
        } else if (mentionChar === "#") {
          values = props.channels;
        } else if (
          mentionChar === "{{" &&
          props.personalizationValues?.length
        ) {
          values = props.personalizationValues;
        }
        // search through list, only render first 10 matches for performance
        if (searchTerm.length === 0) {
          renderList(values.slice(0, 10), mentionChar + searchTerm);
        } else {
          const matches = [];
          for (let i = 0; i < values.length; i += 1)
            if (
              values[i].value
                //This regex remove emojis from the search I got this regex from https://community.folivora.ai/t/removing-emoji-from-string-with-javascript/20976
                .toLowerCase()
                .includes(searchTerm.toLowerCase())
            ) {
              matches.push(values[i]);
            }
          renderList(matches.slice(0, 10), mentionChar + searchTerm);
        }
      },
      [props.members, props.channels]
    ),
  };

  /**
   * Handle editor change by replacing mentions with slack syntax and generating markdown.
   * Calls props.onChange at the end with both HTML and markdown value.
   * @param newHTML
   */
  const onChange = (newHTML: string) => {
    const htmlDoc = parser.parseFromString(newHTML, "text/html");

    // find all mentions in the HTML
    let spans = htmlDoc.querySelector("span[data-denotation-char]");
    // for all spans, replace with member or channel id
    while (spans) {
      const denotation = spans.getAttribute("data-denotation-char");
      if (denotation === " @") {
        const id = spans.getAttribute("data-id");
        const mentionNode = document.createTextNode(` <@${id}>`);
        spans.parentNode && spans.parentNode.replaceChild(mentionNode, spans);
      } else if (denotation === "#") {
        const id = spans.getAttribute("data-id");
        const mentionNode = document.createTextNode(`<#${id}>`);
        spans.parentNode && spans.parentNode.replaceChild(mentionNode, spans);
      } else if (denotation === "{{") {
        const id = spans.getAttribute("data-id");

        ///<@.*>/ is a regex to match discord member mentions for example <@32323232>
        let replacement = /<@.*>/.test(id as string)
          ? //I left this constant user.id value here because that is the only way we can mention a user in discord right now
            `<@{{user.id}}>`
          : `{{${id}}}`;
        const mentionNode = document.createTextNode(replacement);
        spans.parentNode && spans.parentNode.replaceChild(mentionNode, spans);
      }
      spans = htmlDoc.querySelector("span[data-denotation-char]");
    }

    // Every time we create a new line without text quill creates a <p></br></p> element and when
    // we use turndown to convert to markdown this element is converted to two new lines, creating
    // an extra line in the message that is sent to discord, for that reason every time we find a <p></br></p>
    // we just replace it by a <br> element
    const markdown = turndownService.turndown(
      htmlDoc.documentElement.outerHTML.replaceAll("<p><br></p>", "<br>")
    );
    props.onChange({ html: newHTML, text: markdown.replaceAll("\\_", "_") });
  };

  const modules = {
    slack: {
      toolbar: [
        ["bold", "italic"],
        ["link"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["code", "code-block", "blockquote"],
        ["emoji"],
      ],
      clipboard: {
        matchVisual: false,
      },
      mention: mentionModule,
      "emoji-toolbar": true,
      "emoji-shortname": true,
    },
    discord: {
      toolbar: [
        ["italic"],
        ["bold"],
        ["strike"],
        ["code"],
        ["blockquote"],
        ["emoji"],
      ],
      clipboard: {
        matchVisual: false,
      },
      mention: mentionModule,
      "emoji-toolbar": true,
      "emoji-shortname": true,
      "cursor-position": {
        refProp: props.refProp,
      },
      "remove-hyperlinks": true,
    },
  };

  const formats = {
    slack: [
      "bold",
      "italic",
      "strike",
      "link",
      "list",
      "bullet",
      "blockquote",
      "code",
      "code-block",
      "mention",
      "emoji",
    ],
    discord: [
      "bold",
      "italic",
      "strike",
      "link",
      "list",
      "bullet",
      "blockquote",
      "code",
      "code-block",
      "mention",
      "emoji",
    ],
  };

  return (
    <div className="quill-wrapper">
      <ReactQuill
        readOnly={props.readOnly}
        ref={props.refProp}
        theme="snow"
        onChange={onChange}
        value={
          props.templateText
            ? `${props.value} ${props.templateText}`
            : props.value
        }
        modules={modules[props.platform] ? modules[props.platform] : {}}
        formats={formats[props.platform] ? formats[props.platform] : []}
        bounds=".quill-wrapper"
        placeholder={
          props.platform === "slack"
            ? "You can mention users and channels, like in Slack!"
            : "You can mention users and channels, like in Discord"
        }
      />
      {props.children}
    </div>
  );
}

/*
 * Quill editor formats
 * See https://quilljs.com/docs/formats/
 */

export default Editor;
